// The office timesheet (time.day, time.correct): minutes said as hours, what "Ended at ___" sends,
// and a correction's minutes from what the person types. Everything is minutes, never pay.

import { instantAt } from '@flowology/kernel';

export type TimeType = 'work' | 'drive' | 'non_job';

export const TYPE_WORD: Record<TimeType, string> = { work: 'Work', drive: 'Drive', non_job: 'Shop' };

/** 135 → "2 h 15 min"; −30 → "−30 min"; 0 → "0 min". */
export function minutesText(minutes: number): string {
  const sign = minutes < 0 ? '−' : '';
  const m = Math.abs(minutes);
  const h = Math.floor(m / 60);
  const rest = m % 60;
  if (!h) return `${sign}${rest} min`;
  return rest ? `${sign}${h} h ${rest} min` : `${sign}${h} h`;
}

/**
 * "Ended at 16:30" for an entry that started at `startedAt` (on `localDate`, in the company's zone):
 * the minutes to send are the time worked to then, less what the entry already counts (0 for one
 * still open). An end before the start is read as after midnight, the next morning. Null when the
 * time can't be read.
 */
export function endedAtMinutes(
  entry: { startedAt: string; minutes: number },
  localDate: string,
  endTime: string,
  timeZone: string,
): number | null {
  if (!/^\d{2}:\d{2}$/.test(endTime)) return null;
  let end = Date.parse(instantAt(localDate, endTime, timeZone));
  const start = Date.parse(entry.startedAt);
  if (Number.isNaN(end) || Number.isNaN(start)) return null;
  if (end <= start) end += 86_400_000;
  const target = Math.round((end - start) / 60_000);
  return target - entry.minutes;
}

/** Hours and minutes typed, added or taken off: the signed minutes, or null when it isn't 1..1440. */
export function typedMinutes(hours: string, minutes: string, sign: 1 | -1): number | null {
  const h = hours.trim() ? Number(hours) : 0;
  const m = minutes.trim() ? Number(minutes) : 0;
  if (!Number.isInteger(h) || !Number.isInteger(m) || h < 0 || m < 0 || m > 59) return null;
  const total = h * 60 + m;
  return total >= 1 && total <= 1440 ? sign * total : null;
}
